// /**
//  * Authentication Guard Component
//  *
//  * This component handles authentication-related functionality including:
//  * - Idle timeout monitoring
//  * - Auto-logout on inactivity
//  * - Authentication state management
//  */

// "use client";

// import { useEffect, useState } from "react";
// import { useRouter, usePathname } from "next/navigation";
// import {
//   initializeIdleTimeout,
//   stopIdleTimeout,
// } from "@/services/auth/idleTimeoutService";
// import {
//   isAuthenticatedSync,
//   isAuthenticatedUnified,
// } from "@/services/auth/cookieAuthService";
// import { getAuthPaths } from "@/config/basePath";
// import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
// import Loading from "@/app/loading";
// import { DEFAULT_IDEAL_TIMEOUT_MINUTES } from "@/config";

// interface AuthGuardProps {
//   children: React.ReactNode;
//   idleTimeoutMinutes?: number;
// }

// /**
//  * AuthGuard component that wraps the app and provides authentication protection
//  */
// export const AuthGuard: React.FC<AuthGuardProps> = ({
//   children,
//   idleTimeoutMinutes = DEFAULT_IDEAL_TIMEOUT_MINUTES,
// }) => {
//   const router = useRouter();
//   const pathname = usePathname();
//   const { showSuccess } = useAlert();
//   const [isLoading, setIsLoading] = useState(true);
//   const [authChecked, setAuthChecked] = useState(false);

//   // Public routes that don't require authentication
//   const { LOGIN_URL } = getAuthPaths();
//   const publicRoutes = [LOGIN_URL, "/auth/login"]; // Support both basePath and direct routes
//   const isPublicRoute = publicRoutes.includes(pathname);

//   useEffect(() => {
//     // Check authentication status using unified method
//     const checkAuth = async () => {
//       try {
//         // Use the unified authentication check that checks both cookies and localStorage
//         const authenticated = await isAuthenticatedUnified();

//         const { LOGIN_URL, DASHBOARD_URL } = getAuthPaths();

//         // Debug authentication state
//         console.log(
//           `[AuthGuard] Pathname: "${pathname}", Authenticated: ${authenticated}, LOGIN_URL: "${LOGIN_URL}", DASHBOARD_URL: "${DASHBOARD_URL}"`
//         );

//         if (!isPublicRoute && !authenticated) {
//           console.log(
//             "[AuthGuard] Redirecting to login - user not authenticated"
//           );
//           router.push(LOGIN_URL);
//         }
//         // Redirect authenticated users away from login page to dashboard
//         else if (isPublicRoute && authenticated) {
//           console.log(
//             "[AuthGuard] Redirecting authenticated user to dashboard"
//           );
//           showSuccess("You are already logged in.");
//           router.push(DASHBOARD_URL);
//           router.refresh(); // Force refresh to ensure proper navigation
//           // Still set loading to false after a short delay to prevent infinite loading
//           setTimeout(() => {
//             setAuthChecked(true);
//             setIsLoading(false);
//           }, 500);
//           return;
//         }

//         setAuthChecked(true);
//         setIsLoading(false);
//       } catch (error) {
//         console.error("[AuthGuard] Error checking authentication:", error);
//         setAuthChecked(true);
//         setIsLoading(false);
//       }
//     };

//     // Small delay to ensure localStorage is available
//     if (typeof window !== "undefined") {
//       setTimeout(() => {
//         checkAuth();
//       }, 100);
//     }
//   }, [pathname, isPublicRoute, router, showSuccess]);

//   useEffect(() => {
//     // Only initialize idle timeout on authenticated routes
//     if (!isPublicRoute && isAuthenticatedSync()) {
//       initializeIdleTimeout({
//         timeoutMinutes: idleTimeoutMinutes,
//       });
//     } else {
//       stopIdleTimeout();
//     }

//     // Cleanup on unmount
//     return () => {
//       stopIdleTimeout();
//     };
//   }, [pathname, isPublicRoute, idleTimeoutMinutes, authChecked]);

//   // Show loading state until auth is checked and we're not redirecting authenticated users from login
//   // if (isLoading || (isPublicRoute && isAuthenticatedSync())) {
//   //   return <Loading />;
//   // }

//   return <>{children}</>;
// };

// export default AuthGuard;

/**
 * Authentication Guard Component
 *
 * This component handles idle timeout monitoring and auto-logout on inactivity.
 * Authentication checks are handled by middleware to prevent UI flashing.
 */

"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  initializeIdleTimeout,
  stopIdleTimeout,
} from "@/services/auth/idleTimeoutService";
import { isAuthenticatedSync } from "@/services/auth/cookieAuthService";
import { isPublicRoute } from "@/lib/route-config";
import { DEFAULT_IDEAL_TIMEOUT_MINUTES } from "@/config";

interface AuthGuardProps {
  children: React.ReactNode;
  idleTimeoutMinutes?: number;
}

/**
 * AuthGuard component that provides idle timeout protection
 * Authentication redirects are handled by middleware
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  idleTimeoutMinutes = DEFAULT_IDEAL_TIMEOUT_MINUTES,
}) => {
  const pathname = usePathname();

  useEffect(() => {
    // Only initialize idle timeout on protected routes for authenticated users
    const isPublic = isPublicRoute(pathname);
    const isAuthenticated = isAuthenticatedSync();

    if (!isPublic && isAuthenticated) {
      initializeIdleTimeout({
        timeoutMinutes: idleTimeoutMinutes,
      });
    } else {
      stopIdleTimeout();
    }

    // Cleanup on unmount
    return () => {
      stopIdleTimeout();
    };
  }, [pathname, idleTimeoutMinutes]);

  // Simply render children - middleware handles authentication redirects
  return <>{children}</>;
};

export default AuthGuard;
